'use client';

import React from 'react';
import { FileText, FileSpreadsheet, FileImage, FileArchive, File, Download } from 'lucide-react';
import { Check, CheckCheck } from 'lucide-react';
import { Message } from './types';

interface FileBubbleProps {
  fileUrl: string;
  fileName: string;
  fileSize?: number;
  fileMime?: string;
  isMe: boolean;
  status?: Message['status'];
  timestamp: string;
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getUTCHours();
  const m = d.getUTCMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m} ${ampm}`;
}

function StatusIcon({ status }: { status: Message['status'] }) {
  if (!status) return null;
  if (status === 'sending') return <Check size={12} style={{ color: 'rgba(255,255,255,0.5)' }} aria-label="Sending" />;
  if (status === 'sent') return <Check size={12} style={{ color: 'rgba(255,255,255,0.5)' }} aria-label="Sent" />;
  if (status === 'delivered') return <CheckCheck size={12} style={{ color: 'rgba(255,255,255,0.55)' }} aria-label="Delivered" />;
  if (status === 'read') return <CheckCheck size={12} style={{ color: '#2EA6FF' }} aria-label="Read" />;
  return null;
}

function FileIcon({ mime, size }: { mime?: string; size: number }) {
  const cls = `flex-shrink-0`;
  if (!mime) return <File size={size} className={cls} />;
  if (mime.startsWith('image/')) return <FileImage size={size} className={cls} />;
  if (mime.includes('pdf') || mime.includes('word') || mime.includes('text')) return <FileText size={size} className={cls} />;
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) return <FileSpreadsheet size={size} className={cls} />;
  if (mime.includes('zip') || mime.includes('archive')) return <FileArchive size={size} className={cls} />;
  return <File size={size} className={cls} />;
}

function getFileExtension(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
}

export default function FileBubble({ fileUrl, fileName, fileSize, fileMime, isMe, status, timestamp }: FileBubbleProps) {
  const ext = getFileExtension(fileName);
  const sizeStr = formatFileSize(fileSize);

  return (
    <div
      className="flex items-center gap-3 px-3 py-2.5"
      style={{ minWidth: '220px', maxWidth: '300px' }}
    >
      {/* File icon area */}
      <div
        className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
        style={{
          background: isMe ? 'rgba(255,255,255,0.18)' : 'rgba(46,166,255,0.22)',
        }}
      >
        <FileIcon mime={fileMime} size={22} />
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <p
          className="text-[13.5px] font-medium leading-tight truncate text-white"
          title={fileName}
        >
          {fileName}
        </p>
        <p className="text-[11.5px] leading-none" style={{ color: 'rgba(215,224,234,0.6)' }}>
          {[ext, sizeStr].filter(Boolean).join(' · ')}
        </p>
        {/* Timestamp + status */}
        <span
          className="flex items-center gap-1 text-[11px] leading-none mt-0.5"
          style={{ color: 'rgba(215,224,234,0.55)' }}
        >
          {formatTime(timestamp)}
          {isMe && <StatusIcon status={status} />}
        </span>
      </div>

      {/* Download button */}
      <a
        href={fileUrl}
        download={fileName}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-90"
        style={{ background: 'rgba(255,255,255,0.1)' }}
        aria-label={`Download ${fileName}`}
        title="Download"
      >
        <Download size={15} className="text-white/80" />
      </a>
    </div>
  );
}
